import React from 'react';
import Box from '@mui/material/Box';
import SwipeableDrawer from '@mui/material/SwipeableDrawer';
import List from '@mui/material/List';
import Divider from '@mui/material/Divider';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Typography from '@mui/material/Typography';
import LogOutIcon from '@mui/icons-material/Logout';
import { useTheme, useMediaQuery } from '@mui/material';

import DrawerContent from './DrawerContent';
import drawerConfig from '../drawerConfig';

/**
 * Shared menu list — rendered inside both the permanent desktop drawer
 * and the temporary mobile SwipeableDrawer.
 */
function DrawerMenuList({ menuItems = [], onMenuClick, onLogout }) {
  return (
    <>
      <List>
        {menuItems.map((link, index) => (
          <ListItemButton
            key={link.link || link.text || index}
            onClick={() => onMenuClick(link.link)}
            title={link.text || ''}
            sx={{ minHeight: 48, px: 2.5 }}
          >
            <ListItemIcon
              sx={{
                minWidth: 0,
                mr: 3,
                justifyContent: 'center',
                width: 40,
                height: 40,
                borderRadius: '50%',
                backgroundColor: '#ede7f6',
                display: 'flex',
                alignItems: 'center',
                '& svg': { color: '#673ab7', fontSize: '1.5rem' },
              }}
            >
              {link.icon}
            </ListItemIcon>
            <ListItemText
              primary={link.text}
              primaryTypographyProps={{
                fontSize: '1rem',
                fontWeight: 600,
                lineHeight: 1.3,
              }}
            />
          </ListItemButton>
        ))}
      </List>
      <Divider />
      <List>
        <ListItemButton onClick={onLogout} title="Log Out" sx={{ minHeight: 48, px: 2.5 }}>
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: 3,
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: '50%',
              backgroundColor: '#ede7f6',
              display: 'flex',
              alignItems: 'center',
              '& svg': { color: '#673ab7', fontSize: '1.5rem' },
            }}
          >
            <LogOutIcon />
          </ListItemIcon>
          <ListItemText
            primary="Log Out"
            primaryTypographyProps={{
              fontSize: '1rem',
              fontWeight: 600,
              lineHeight: 1.3,
            }}
          />
        </ListItemButton>
      </List>
    </>
  );
}

/**
 * ResponsiveDrawer — app-wide navigation drawer.
 *
 * md+ (desktop / tablet-landscape):
 *   The existing permanent mini drawer — icons-only when collapsed (65 px),
 *   full labels when expanded (240 px). Unchanged from the original.
 *
 * xs/sm (phone):
 *   A SwipeableDrawer that slides in from the left on a hamburger tap.
 *   It is `temporary` so it overlays content without affecting layout width.
 *   Closes automatically after a menu item is tapped.
 *
 * Props:
 *   open           {boolean}  — desktop mini-drawer expanded state
 *   showScrollbar  {boolean}  — desktop scrollbar visibility
 *   onToggle       {function} — desktop toggle (expand/collapse)
 *   mobileOpen     {boolean}  — mobile drawer open state
 *   onMobileClose  {function} — close mobile drawer
 *   menuItems      {array}
 *   onMenuClick    {function}
 *   onLogout       {function}
 *   onMouseMove    {function} — desktop scrollbar show handler
 *   onMouseLeave   {function} — desktop scrollbar hide handler
 */
export default function ResponsiveDrawer({
  open,
  showScrollbar,
  onToggle,
  mobileOpen,
  onMobileClose,
  menuItems = [],
  onMenuClick,
  onLogout,
  onMouseMove,
  onMouseLeave,
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Close the mobile drawer then navigate
  const handleMobileMenuClick = (link) => {
    onMobileClose?.();
    onMenuClick?.(link);
  };

  const handleMobileLogout = () => {
    onMobileClose?.();
    onLogout?.();
  };

  if (isMobile) {
    return (
      <SwipeableDrawer
        anchor="left"
        open={!!mobileOpen}
        onOpen={() => {}} // controlled externally via hamburger
        onClose={onMobileClose}
        // Slightly wider than the mini drawer so labels are comfortable
        PaperProps={{
          sx: {
            width: drawerConfig.width,
            pt: `${64}px`, // clear the fixed AppBar
            pb: `env(safe-area-inset-bottom, 0px)`,
          },
        }}
        // Keep the DOM mounted for performance (avoids re-mount on every open)
        ModalProps={{ keepMounted: true }}
      >
        {/* App name header */}
        <Box sx={{ px: 2.5, py: 1.5 }}>
          <Typography variant="subtitle2" color="text.secondary" noWrap>
            Rev.Jet 360
          </Typography>
        </Box>
        <Divider />
        <DrawerMenuList
          menuItems={menuItems}
          onMenuClick={handleMobileMenuClick}
          onLogout={handleMobileLogout}
        />
      </SwipeableDrawer>
    );
  }

  // Desktop: original permanent mini drawer
  return (
    <DrawerContent
      open={open}
      showScrollbar={showScrollbar}
      onToggle={onToggle}
      menuItems={menuItems}
      onMenuClick={onMenuClick}
      onLogout={onLogout}
      onMouseMove={onMouseMove}
      onMouseLeave={onMouseLeave}
    />
  );
}
